import { EVENTS, STORAGE_KEYS } from "../../../js/constants";

class ContrastSwitcher {
  /**
   * @param {HTMLElement} element - the contrast switcher element
   */
  constructor(element) {
    this.element = element;

    this.config = {
      selectors: {
        buttonSelector: ".js-ContrastSwitcher-button",
        checkboxSelector: ".js-ContrastSwitcher-checkbox",
        contentSelector: ".js-ContrastSwitcher-content",
        increasedContrastSelector: ".js-ContrastSwitcher-increasedContrast",
        labelSelector: ".js-ContrastSwitcher-label",
      },
      classes: {
        open: "is-open",
      },
    };

    this.button = element.querySelector(this.config.selectors.buttonSelector);
    this.content = element.querySelector(this.config.selectors.contentSelector);
    this.label = element.querySelector(this.config.selectors.labelSelector);
    this.increasedContrastElement = element.querySelector(
      this.config.selectors.increasedContrastSelector
    );
    this.checkbox = element.querySelector(
      this.config.selectors.checkboxSelector
    );

    this._openCloseContent = this._openCloseContent.bind(this);
    this._toggleSwitcher = this._toggleSwitcher.bind(this);

    this.open = false;

    this._bind();
    this._manageChosenContrast();

    // events for closing the contrast switcher via keyboard or click somewhere on the body
    this.keyUpListener = this.onKeyUp.bind(this);
    this.clickListener = this.onClick.bind(this);
  }

  /**
   * binds events
   *
   * @private
   */
  _bind() {
    this.button.addEventListener("click", () => this._openCloseContent());
    this.checkbox.addEventListener("change", this._toggleSwitcher);
  }

  /**
   * opens/closes the dropdown on the click on `Accessibility` button
   *
   * @param {boolean} forcedState
   * @private
   */
  _openCloseContent(forcedState) {
    const state = forcedState ?? !this.open;

    this.button.setAttribute("aria-expanded", `${state}`);
    this.content.setAttribute("aria-hidden", `${!state}`);

    this.element.classList.toggle(this.config.classes.open, state);

    if (state) {
      window.addEventListener("keyup", this.keyUpListener);
      document.addEventListener("click", this.clickListener);
    } else {
      window.removeEventListener("keyup", this.keyUpListener);
      document.removeEventListener("click", this.clickListener);
    }

    this.open = !this.open;
  }

  /**
   * closes the contrast switcher when pressing ESCAPE
   *
   * @param {Event} e
   */
  onKeyUp(e) {
    if (e.keyCode === 27) {
      this._openCloseContent(false);
    }
  }

  /**
   * closes the contrast switcher when clicking somewhere in the body,
   * but not the contrast switcher itself
   *
   * @param {Event} e
   */
  onClick(e) {
    if (!this.element.contains(e.target)) {
      this._openCloseContent(false);
    }
  }

  /**
   * controls which contrast should be used depending
   * on the operating system settings or (and) the user's choice on the web-site;
   *
   * @private
   */
  _manageChosenContrast() {
    const contrastStorage = window.localStorage.getItem(STORAGE_KEYS.CONTRAST);

    if (contrastStorage) {
      // TODO: Replace next line with `this._render(contrastStorage);` after custom events refactoring;
      document.documentElement.classList.add(`contrast-${contrastStorage}`);
      this.checkbox.checked = contrastStorage === "high";
    } else if (ContrastSwitcher._isPreferredIncreasedContrast()) {
      this._render("high");
      this.checkbox.checked = true;
    }
  }

  /**
   * Set the correct switcher label text and dispatch custom event
   *
   * @private
   * @param {string} contrast
   */
  _render(contrast) {
    this.label.textContent = this.label.getAttribute(`data-title-${contrast}`);
    document.documentElement.dispatchEvent(
      new CustomEvent(EVENTS.CONTRAST, {
        detail: { contrast },
      })
    );
  }

  /**
   * Sets item in local storage with preferred contrast
   *
   * @private
   */
  _toggleSwitcher() {
    const contrast = this.checkbox.checked ? "high" : "low";
    this._render(contrast);
    window.localStorage.setItem(STORAGE_KEYS.CONTRAST, contrast);
  }

  /**
   * Checks if there is "increased contrast" set in the operating system
   *
   * @static
   * @returns {boolean}
   */
  static _isPreferredIncreasedContrast() {
    return window.matchMedia("(prefers-contrast: more)").matches;
  }
}

export default ContrastSwitcher;
